import originalTextField from '../material_design/text_field'

// Link to discussion piece used as inspiration for some of the cursor handling logic:
// https://michaellaszlo.com/cursor-maintenance/introduction/

const formatInteger = {
  ...originalTextField,
  setup() {
    this.storedValue = ''
    this.handleIntegerInputWrapper = (event) => this.handleIntegerInput(event)

    // setup our various handlers.  The main one is the input event where we account for changes
    // in the textfield itself (i.e. adding numbers or deleting numbers and commas)
    //
    // We add a beforeinput callback to check that we're only adding numbers to the input.
    //
    // We also listen to the keydown specifically to modify our behavior for moving the cursor left
    // and right
    //
    // see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key#keyboardevent_sequence
    // for more details on the keyboard event sequences.

    this.el.addEventListener('input', this.handleIntegerInputWrapper)
    this.el.addEventListener('beforeinput', restrictToIntegers)
    this.el.addEventListener('keydown', skipCommas)

    // call the original material design code to setup those handlers properly.
    originalTextField.setup.call(this)

    // also format the initial value.  We only do this for values which are integers and not empty
    // strings.  We want to leave an empty string alone rather than turn it into a zero.
    const value = stripCommas(this.textField.value)
    if (!isNaN(value) && value !== '') {
      this.textField.value = formatIntegerWithCommas(value)
    }

    this.storedValue = this.textField.value
  },

  destroy() {
    this.el.removeEventListener('input', this.handleIntegerInputWrapper)
    this.el.removeEventListener('beforeinput', restrictToIntegers)
    this.el.removeEventListener('keydown', skipCommas)
    originalTextField.destroy?.call(this)
  },

  handleIntegerInput(event) {
    let input = event.target
    let textFieldValue = input.value
    let value = stripCommas(input.value)
    let cursorPosition = input.selectionStart
    let digitCountBeforeCursor = countDigitsBeforePosition(
      textFieldValue,
      cursorPosition
    )

    if (!isNaN(value) && value !== '') {
      if (oneCommaDeleted(this.storedValue, textFieldValue)) {
        // here we handle deleing an "extra" char.  Basically if the user
        // is deleting "through" a comma, we delete the number they would
        // have deleted if the comma wasn't present.
        if (event.inputType === 'deleteContentForward') {
          value = stripCommas(
            textFieldValue.slice(0, cursorPosition) +
              textFieldValue.slice(cursorPosition + 1)
          )
        } else {
          value = stripCommas(
            textFieldValue.slice(0, cursorPosition - 1) +
              textFieldValue.slice(cursorPosition)
          )

          digitCountBeforeCursor -= 1
        }
      }

      const formatted = commatize(value, digitCountBeforeCursor)
      input.value = formatted.text
      this.storedValue = formatted.text

      // Restore the cursor position
      input.setSelectionRange(formatted.cursor, formatted.cursor)
    }
  }
}

// Private helper functions
function formatIntegerWithCommas(integer) {
  return Number(integer).toLocaleString('en')
}

function restrictToIntegers(event) {
  if (event.data && !/[0-9]/.test(event.data)) {
    event.preventDefault()
  }
}

function stripCommas(string) {
  return string.replace(/,/g, '')
}

function skipCommas(event) {
  if (event.key === 'ArrowLeft') {
    const input = event.target
    const cursorPosition = input.selectionStart
    // if we are moving left, and we would end up just before a comma, move to after the comma
    if (input.value.charAt(cursorPosition - 2) === ',') {
      event.preventDefault()
      input.setSelectionRange(cursorPosition - 2, cursorPosition - 2)
    }
  }
  if (event.key === 'ArrowRight') {
    const input = event.target
    const cursorPosition = input.selectionStart
    if (input.value.charAt(cursorPosition + 1) === ',') {
      event.preventDefault()
      input.setSelectionRange(cursorPosition + 2, cursorPosition + 2)
    }
  }
}

function countDigitsBeforePosition(string, position) {
  return stripCommas(string.substring(0, position)).length
}

function commatize(integerString, leftDigitCount) {
  let pos, ch
  let s = formatIntegerWithCommas(integerString)

  if (leftDigitCount == 0) {
    return { text: s, cursor: 0 }
  }
  // Count off digit characters.
  for (pos = 0; pos < s.length; ++pos) {
    ch = s.charAt(pos)
    if (ch !== ',') {
      if (--leftDigitCount === 0) {
        break
      }
    }
  }
  // Place the cursor to the right of the counted digits.
  cursor = pos + 1
  return { text: s, cursor: cursor }
}

function oneCommaDeleted(currentString, newString) {
  if (currentString.length - newString.length !== 1) {
    return false
  }

  for (let i = 0; i < newString.length; i++) {
    if (currentString[i] !== newString[i]) {
      return currentString[i] === ','
    }
  }

  return false
}

export default formatInteger
